import { useCallback } from 'react';

import InfoOutlined from '@mui/icons-material/InfoOutlined';
import { useTheme } from '@mui/material/styles';
import { generateName } from 'config/requests';
import { ReactComponent as GenerateTextSVG } from 'images/generateText.svg';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { TextInputProps, useInput } from 'react-admin';
import { useWatch } from 'react-hook-form';
import { IconButton } from 'shared/mui/IconButtons';
import { Tooltip } from 'shared/mui/Tooltip/Tooltip/Tooltip';
import { Typography } from 'shared/mui/Typography';
import { TextInput } from 'shared/react-admin/Inputs';

export type MerchantShowAccountsNameInputProps = {
  merchantId?: string;
} & TextInputProps;

export function MerchantShowAccountsNameInput({
  merchantId,
  ...rest
}: MerchantShowAccountsNameInputProps) {
  const translate = useTypedTranslate();

  const { field } = useInput({ source: rest.source });
  const theme = useTheme();
  const [provider, currency] = useWatch({ name: ['provider', 'currency'] });

  const handleClick = useCallback(async () => {
    const response = await generateName('merchant_account', {
      merchant: merchantId,
      provider: provider,
      currency: currency,
    });
    field.onChange(response.data.name);
  }, [merchantId, provider, currency]);

  return (
    <TextInput
      {...rest}
      InputProps={{
        endAdornment: [
          <Tooltip
            key="generate-name"
            title={
              <Typography fontSize={12}>
                {translate('app.merchantAccounts.generateName')}
              </Typography>
            }
          >
            <span>
              <IconButton disabled={!provider || !currency} onClick={handleClick}>
                <GenerateTextSVG
                  style={{ color: provider && currency ? theme.palette.primary.main : 'gray' }}
                />
              </IconButton>
            </span>
          </Tooltip>,
          <Tooltip
            arrow
            key="info"
            title={
              <Typography fontSize={12}>
                {translate('app.merchantAccounts.generateAccountNameHint')}
              </Typography>
            }
          >
            <InfoOutlined sx={{ height: 26, width: 26, color: 'gray' }} />
          </Tooltip>,
        ],
      }}
      size="small"
    />
  );
}
